/** @module @liquidefy/code-editor/ai/ai-agents @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  AIAgentState,
  AIAgentType,
  AIAgentStatus,
  AIAgentTask,
  AIAgentConfig,
  AIAgentMemory,
  AIAgentPerformance,
  AIAgentId,
  AIEpisode,
  AIExecutionStep,
  AIExecutionPlan,
  AITaskResult,
  AIPriority,
  AIConstraints,
  EditorResult,
} from '../../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asAIAgentId,
  now,
  emptyAIAgentState,
  agentCapabilities as engineAgentCapabilities,
  createExecutionPlan as engineCreatePlan,
  advancePlan as engineAdvancePlan,
} from '../../code-editor.js';

// ═══ §2 AGENT CREATION — construct and configure agents ═══

/**
 * Default configuration per agent type.
 */
export const AGENT_DEFAULTS: Record<AIAgentType, Partial<AIAgentConfig>> = {
  'code-generator': { maxConcurrentTasks: 3, learningRate: 0.1, autonomyLevel: 'semi-autonomous' },
  'code-reviewer': { maxConcurrentTasks: 2, learningRate: 0.05, autonomyLevel: 'supervised' },
  'test-generator': { maxConcurrentTasks: 2, learningRate: 0.1, autonomyLevel: 'semi-autonomous' },
  'refactoring-expert': { maxConcurrentTasks: 1, learningRate: 0.08, autonomyLevel: 'supervised' },
  'debugging-specialist': { maxConcurrentTasks: 1, learningRate: 0.15, autonomyLevel: 'semi-autonomous' },
  'security-auditor': { maxConcurrentTasks: 2, learningRate: 0.05, autonomyLevel: 'supervised' },
  'performance-optimizer': { maxConcurrentTasks: 1, learningRate: 0.1, autonomyLevel: 'semi-autonomous' },
  'project-manager': { maxConcurrentTasks: 5, learningRate: 0.05, autonomyLevel: 'autonomous' },
  'architect': { maxConcurrentTasks: 1, learningRate: 0.05, autonomyLevel: 'supervised' },
  'devops-engineer': { maxConcurrentTasks: 3, learningRate: 0.1, autonomyLevel: 'semi-autonomous' },
  'qa-engineer': { maxConcurrentTasks: 3, learningRate: 0.08, autonomyLevel: 'semi-autonomous' },
  'documentation-writer': { maxConcurrentTasks: 3, learningRate: 0.1, autonomyLevel: 'autonomous' },
};

/**
 * Create a new agent with sensible defaults for its type.
 */
export function createAgent(id: string, type: AIAgentType, config?: Partial<AIAgentConfig>): AIAgentState {
  const agentId = asAIAgentId(id);
  const base = emptyAIAgentState(agentId, type);
  const defaults = AGENT_DEFAULTS[type] ?? {};
  return {
    ...base,
    config: {
      maxConcurrentTasks: config?.maxConcurrentTasks ?? defaults.maxConcurrentTasks ?? 1,
      learningRate: config?.learningRate ?? defaults.learningRate ?? 0.1,
      autonomyLevel: config?.autonomyLevel ?? defaults.autonomyLevel ?? 'supervised',
      modelPreference: config?.modelPreference,
    },
  };
}

/**
 * Reconfigure an agent with new config values.
 */
export function configureAgent(state: AIAgentState, config: Partial<AIAgentConfig>): AIAgentState {
  return {
    ...state,
    config: {
      ...state.config,
      ...config,
    },
  };
}

/**
 * Reset an agent to its initial state while preserving ID, type, and config.
 */
export function resetAgent(state: AIAgentState): AIAgentState {
  return {
    ...state,
    status: 'idle',
    currentTask: undefined,
    memory: { shortTerm: {}, longTerm: {}, episodic: [], working: [] },
    performance: { tasksCompleted: 0, tasksSucceeded: 0, averageDuration: 0, averageQuality: 0 },
  };
}

/**
 * Clone an agent with a new ID.
 */
export function cloneAgent(state: AIAgentState, newId: string): AIAgentState {
  return {
    ...state,
    id: asAIAgentId(newId),
    name: `${state.name}-clone`,
    status: 'idle',
    currentTask: undefined,
  };
}

// ═══ §3 AGENT LIFECYCLE — state transitions ═══

/**
 * Valid state transitions for agents.
 */
export const VALID_AGENT_TRANSITIONS: Record<AIAgentStatus, readonly AIAgentStatus[]> = {
  idle: ['planning', 'executing', 'paused', 'terminated'],
  planning: ['executing', 'idle', 'error', 'paused', 'terminated'],
  executing: ['idle', 'waiting', 'learning', 'error', 'paused', 'terminated'],
  waiting: ['executing', 'idle', 'error', 'paused', 'terminated'],
  learning: ['idle', 'executing', 'error', 'paused', 'terminated'],
  error: ['idle', 'terminated'],
  paused: ['idle', 'executing', 'planning', 'terminated'],
  terminated: [],
};

/**
 * Transition an agent to a new status, validating the transition.
 */
export function transitionAgent(state: AIAgentState, newStatus: AIAgentStatus): EditorResult<AIAgentState> {
  const allowed = VALID_AGENT_TRANSITIONS[state.status];
  if (!allowed.includes(newStatus)) {
    return err('INVALID_TRANSITION', `Cannot transition from ${state.status} to ${newStatus}`);
  }
  return ok({ ...state, status: newStatus }, 'AGENT_TRANSITIONED', `Agent transitioned to ${newStatus}`);
}

/**
 * Activate an idle or paused agent.
 */
export function activateAgent(state: AIAgentState): EditorResult<AIAgentState> {
  if (state.status === 'idle' || state.status === 'paused') {
    return ok({ ...state, status: 'planning' }, 'AGENT_ACTIVATED', 'Agent activated');
  }
  return err('INVALID_STATE', `Cannot activate agent in ${state.status} state`);
}

/**
 * Pause a running agent.
 */
export function pauseAgent(state: AIAgentState): EditorResult<AIAgentState> {
  return transitionAgent(state, 'paused');
}

/**
 * Terminate an agent.
 */
export function terminateAgent(state: AIAgentState): EditorResult<AIAgentState> {
  if (state.status === 'terminated') {
    return err('ALREADY_TERMINATED', 'Agent is already terminated');
  }
  return ok({ ...state, status: 'terminated', currentTask: undefined }, 'AGENT_TERMINATED', 'Agent terminated');
}

/**
 * Check if agent is idle.
 */
export function isAgentIdle(state: AIAgentState): boolean {
  return state.status === 'idle';
}

/**
 * Check if agent is busy (executing, planning, waiting, or learning).
 */
export function isAgentBusy(state: AIAgentState): boolean {
  return state.status === 'executing' || state.status === 'planning' || state.status === 'waiting' || state.status === 'learning';
}

/**
 * Check if agent is healthy (not in error or terminated state).
 */
export function isAgentHealthy(state: AIAgentState): boolean {
  return state.status !== 'error' && state.status !== 'terminated';
}

/**
 * Calculate agent uptime in milliseconds since status was last not idle/terminated.
 */
export function agentUptime(state: AIAgentState, currentTime: number): number {
  if (state.status === 'idle' || state.status === 'terminated') return 0;
  // Use first episode timestamp as proxy for start time
  if (state.memory.episodic.length === 0) return 0;
  const earliest = state.memory.episodic.reduce((min, ep) => Math.min(min, ep.timestamp), Infinity);
  return currentTime - earliest;
}

// ═══ §4 TASK MANAGEMENT — assign, track, complete tasks ═══

/**
 * Assign a task to the agent.
 */
export function assignTask(state: AIAgentState, task: AIAgentTask): EditorResult<AIAgentState> {
  if (state.currentTask && state.currentTask.progress < 1) {
    return err('TASK_IN_PROGRESS', 'Agent already has an active task');
  }
  if (state.status === 'terminated') {
    return err('AGENT_TERMINATED', 'Cannot assign task to terminated agent');
  }
  return ok(
    { ...state, currentTask: { ...task, progress: 0 }, status: 'planning' },
    'TASK_ASSIGNED',
    `Task ${task.id} assigned`,
  );
}

/**
 * Start executing the current task.
 */
export function startTask(state: AIAgentState): EditorResult<AIAgentState> {
  if (!state.currentTask) {
    return err('NO_TASK', 'No task assigned to start');
  }
  return ok({ ...state, status: 'executing' }, 'TASK_STARTED', 'Task execution started');
}

/**
 * Complete the current task with a result.
 */
export function completeTask(state: AIAgentState, result: AITaskResult): EditorResult<AIAgentState> {
  if (!state.currentTask) {
    return err('NO_TASK', 'No task to complete');
  }
  const updatedTask: AIAgentTask = { ...state.currentTask, progress: 1, result };
  const perf = recordCompletionInternal(state.performance, result.success, result.duration, 0.7);
  return ok(
    { ...state, currentTask: updatedTask, status: 'idle', performance: perf },
    'TASK_COMPLETED',
    `Task ${state.currentTask.id} completed`,
  );
}

/**
 * Fail the current task.
 */
export function failTask(state: AIAgentState, error: string): EditorResult<AIAgentState> {
  if (!state.currentTask) {
    return err('NO_TASK', 'No task to fail');
  }
  const failResult: AITaskResult = {
    success: false,
    output: error,
    artifacts: [],
    duration: 0,
    tokensUsed: 0,
  };
  const updatedTask: AIAgentTask = { ...state.currentTask, result: failResult };
  const perf = recordCompletionInternal(state.performance, false, 0, 0);
  return ok(
    { ...state, currentTask: updatedTask, status: 'error', performance: perf },
    'TASK_FAILED',
    `Task ${state.currentTask.id} failed: ${error}`,
  );
}

/**
 * Cancel the current task.
 */
export function cancelTask(state: AIAgentState): EditorResult<AIAgentState> {
  if (!state.currentTask) {
    return err('NO_TASK', 'No task to cancel');
  }
  return ok(
    { ...state, currentTask: undefined, status: 'idle' },
    'TASK_CANCELLED',
    'Current task cancelled',
  );
}

/**
 * Get task progress (0-1), returns 0 if no task.
 */
export function getTaskProgress(state: AIAgentState): number {
  return state.currentTask?.progress ?? 0;
}

/**
 * Estimate task duration based on similar past tasks.
 */
export function estimateTaskDuration(
  task: AIAgentTask,
  history: readonly { readonly type: string; readonly duration: number }[],
): number {
  const similar = history.filter((h) => h.type === task.type);
  if (similar.length === 0) return task.constraints?.maxLatencyMs ?? 30000;
  return similar.reduce((sum, h) => sum + h.duration, 0) / similar.length;
}

/**
 * Calculate remaining time for the current task.
 */
export function taskTimeRemaining(state: AIAgentState, currentTime: number): number {
  if (!state.currentTask) return 0;
  const deadline = state.currentTask.deadline;
  if (!deadline) return Infinity;
  return Math.max(0, deadline - currentTime);
}

/**
 * Check if the current task is overdue.
 */
export function isTaskOverdue(state: AIAgentState, currentTime: number): boolean {
  if (!state.currentTask?.deadline) return false;
  return currentTime > state.currentTask.deadline;
}

/**
 * Sort tasks by priority (highest first), then by deadline.
 */
export function taskPrioritySort(tasks: readonly AIAgentTask[]): AIAgentTask[] {
  const priorityOrder: Record<AIPriority, number> = { low: 0, normal: 1, high: 2, critical: 3 };
  return [...tasks].sort((a, b) => {
    const pDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
    if (pDiff !== 0) return pDiff;
    const aDeadline = a.deadline ?? Infinity;
    const bDeadline = b.deadline ?? Infinity;
    return aDeadline - bDeadline;
  });
}

// ═══ §5 AGENT MEMORY — short-term, long-term, episodic ═══

/**
 * Update a key in short-term memory.
 */
export function updateShortTermMemory(state: AIAgentState, key: string, value: unknown): AIAgentState {
  return {
    ...state,
    memory: {
      ...state.memory,
      shortTerm: { ...state.memory.shortTerm, [key]: value },
    },
  };
}

/**
 * Update a key in long-term memory.
 */
export function updateLongTermMemory(state: AIAgentState, key: string, value: unknown): AIAgentState {
  return {
    ...state,
    memory: {
      ...state.memory,
      longTerm: { ...state.memory.longTerm, [key]: value },
    },
  };
}

/**
 * Clear short-term memory.
 */
export function clearShortTermMemory(state: AIAgentState): AIAgentState {
  return {
    ...state,
    memory: { ...state.memory, shortTerm: {} },
  };
}

/**
 * Retrieve a value from memory (checks short-term first, then long-term).
 */
export function getFromMemory(state: AIAgentState, key: string): unknown {
  if (key in state.memory.shortTerm) return state.memory.shortTerm[key];
  if (key in state.memory.longTerm) return state.memory.longTerm[key];
  return undefined;
}

/**
 * Add an episode to episodic memory.
 */
export function addEpisode(state: AIAgentState, episode: AIEpisode): AIAgentState {
  return {
    ...state,
    memory: { ...state.memory, episodic: [...state.memory.episodic, episode] },
  };
}

/**
 * Find episodes relevant to the given context, sorted by relevance descending.
 */
export function relevantEpisodes(
  state: AIAgentState,
  context: string,
  maxCount: number = 10,
): AIEpisode[] {
  const contextWords = new Set(context.toLowerCase().split(/\s+/));
  const scored = state.memory.episodic.map((ep) => {
    const epWords = new Set(ep.context.toLowerCase().split(/\s+/));
    let overlap = 0;
    for (const w of contextWords) {
      if (epWords.has(w)) overlap++;
    }
    const relevance = contextWords.size > 0 ? overlap / contextWords.size : 0;
    return { episode: ep, score: relevance * ep.relevance };
  });
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, maxCount).map((s) => s.episode);
}

/**
 * Remove episodes older than maxAge milliseconds.
 */
export function pruneOldEpisodes(state: AIAgentState, maxAge: number): AIAgentState {
  const cutoff = now() - maxAge;
  const filtered = state.memory.episodic.filter((ep) => ep.timestamp >= cutoff);
  return {
    ...state,
    memory: { ...state.memory, episodic: filtered },
  };
}

/**
 * Estimate memory size (rough count of entries).
 */
export function memorySize(state: AIAgentState): number {
  const stKeys = Object.keys(state.memory.shortTerm).length;
  const ltKeys = Object.keys(state.memory.longTerm).length;
  const epCount = state.memory.episodic.length;
  const wkCount = state.memory.working.length;
  return stKeys + ltKeys + epCount + wkCount;
}

/**
 * Push an item onto working memory.
 */
export function pushWorkingMemory(state: AIAgentState, item: unknown): AIAgentState {
  return {
    ...state,
    memory: { ...state.memory, working: [...state.memory.working, item] },
  };
}

/**
 * Pop the last item from working memory.
 */
export function popWorkingMemory(state: AIAgentState): { readonly state: AIAgentState; readonly item: unknown } {
  if (state.memory.working.length === 0) {
    return { state, item: undefined };
  }
  const items = [...state.memory.working];
  const item = items.pop();
  return {
    state: { ...state, memory: { ...state.memory, working: items } },
    item,
  };
}

// ═══ §6 EXECUTION PLANS — create, advance, query plan state ═══

/**
 * Create an execution plan from a list of steps.
 */
export function createPlan(steps: readonly AIExecutionStep[]): AIExecutionPlan {
  return engineCreatePlan([...steps]);
}

/**
 * Advance a step within the plan to a new status.
 */
export function advanceStep(
  plan: AIExecutionPlan,
  stepId: string,
  status: AIExecutionStep['status'],
): AIExecutionPlan {
  return engineAdvancePlan(plan, stepId, status);
}

/**
 * Get the currently running step (first step with status 'running').
 */
export function currentStep(plan: AIExecutionPlan): AIExecutionStep | null {
  return plan.steps.find((s) => s.status === 'running') ?? null;
}

/**
 * Get the next pending step (first step with status 'pending' whose deps are met).
 */
export function nextStep(plan: AIExecutionPlan): AIExecutionStep | null {
  const completedIds = new Set(plan.steps.filter((s) => s.status === 'completed').map((s) => s.id));
  return plan.steps.find((s) => {
    if (s.status !== 'pending') return false;
    return s.dependencies.every((dep) => completedIds.has(dep));
  }) ?? null;
}

/**
 * Check if every step in the plan is completed or skipped.
 */
export function isPlanComplete(plan: AIExecutionPlan): boolean {
  return plan.steps.every((s) => s.status === 'completed' || s.status === 'skipped');
}

/**
 * Check if any step has failed.
 */
export function isPlanFailed(plan: AIExecutionPlan): boolean {
  return plan.steps.some((s) => s.status === 'failed');
}

/**
 * Plan progress 0-1.
 */
export function planProgress(plan: AIExecutionPlan): number {
  if (plan.steps.length === 0) return 1;
  const done = plan.steps.filter((s) => s.status === 'completed' || s.status === 'skipped').length;
  return done / plan.steps.length;
}

/**
 * Count remaining (pending + running) steps.
 */
export function remainingSteps(plan: AIExecutionPlan): number {
  return plan.steps.filter((s) => s.status === 'pending' || s.status === 'running').length;
}

/**
 * Estimate total remaining duration for the plan.
 */
export function estimatePlanDuration(plan: AIExecutionPlan): number {
  return plan.steps
    .filter((s) => s.status === 'pending' || s.status === 'running')
    .reduce((sum, s) => sum + s.estimatedDuration, 0);
}

/**
 * Add a step to the plan, optionally after a specific step.
 */
export function addStep(
  plan: AIExecutionPlan,
  step: AIExecutionStep,
  afterId?: string,
): AIExecutionPlan {
  const steps = [...plan.steps];
  if (afterId) {
    const idx = steps.findIndex((s) => s.id === afterId);
    if (idx >= 0) {
      steps.splice(idx + 1, 0, step);
    } else {
      steps.push(step);
    }
  } else {
    steps.push(step);
  }
  const totalDuration = steps.reduce((sum, s) => sum + s.estimatedDuration, 0);
  return { ...plan, steps, estimatedDuration: totalDuration };
}

/**
 * Remove a step from the plan.
 */
export function removeStep(plan: AIExecutionPlan, stepId: string): AIExecutionPlan {
  const steps = plan.steps.filter((s) => s.id !== stepId);
  // Remove dependencies on the removed step
  const cleaned = steps.map((s) => ({
    ...s,
    dependencies: s.dependencies.filter((d) => d !== stepId),
  }));
  const totalDuration = cleaned.reduce((sum, s) => sum + s.estimatedDuration, 0);
  return { ...plan, steps: cleaned, estimatedDuration: totalDuration };
}

/**
 * Reorder steps according to the given ID order.
 */
export function reorderSteps(plan: AIExecutionPlan, stepIds: readonly string[]): AIExecutionPlan {
  const byId = new Map(plan.steps.map((s) => [s.id, s]));
  const ordered: AIExecutionStep[] = [];
  for (const id of stepIds) {
    const step = byId.get(id);
    if (step) ordered.push(step);
  }
  // Append any steps not in the reorder list
  for (const step of plan.steps) {
    if (!stepIds.includes(step.id)) ordered.push(step);
  }
  return { ...plan, steps: ordered };
}

// ═══ §7 AGENT CAPABILITIES — query and modify capability sets ═══

/**
 * Get the default capabilities for an agent type.
 */
export function agentCapabilities(type: AIAgentType): string[] {
  return engineAgentCapabilities(type);
}

/**
 * Check if the agent has a specific capability.
 */
export function hasCapability(state: AIAgentState, capability: string): boolean {
  return state.capabilities.includes(capability);
}

/**
 * Add a capability to an agent.
 */
export function addCapability(state: AIAgentState, capability: string): AIAgentState {
  if (state.capabilities.includes(capability)) return state;
  return { ...state, capabilities: [...state.capabilities, capability] };
}

/**
 * Remove a capability from an agent.
 */
export function removeCapability(state: AIAgentState, capability: string): AIAgentState {
  return { ...state, capabilities: state.capabilities.filter((c) => c !== capability) };
}

/**
 * Match required capabilities against available ones.
 */
export function matchCapabilities(
  required: readonly string[],
  available: readonly string[],
): { readonly matched: string[]; readonly missing: string[] } {
  const availSet = new Set(available);
  const matched: string[] = [];
  const missing: string[] = [];
  for (const r of required) {
    if (availSet.has(r)) matched.push(r);
    else missing.push(r);
  }
  return { matched, missing };
}

// ═══ §8 PERFORMANCE TRACKING — success rate, quality, duration ═══

function recordCompletionInternal(
  perf: AIAgentPerformance,
  success: boolean,
  duration: number,
  quality: number,
): AIAgentPerformance {
  const total = perf.tasksCompleted + 1;
  const succeeded = perf.tasksSucceeded + (success ? 1 : 0);
  const avgDur = (perf.averageDuration * perf.tasksCompleted + duration) / total;
  const avgQual = (perf.averageQuality * perf.tasksCompleted + quality) / total;
  return {
    tasksCompleted: total,
    tasksSucceeded: succeeded,
    averageDuration: avgDur,
    averageQuality: avgQual,
  };
}

/**
 * Record a task completion and update performance metrics.
 */
export function recordCompletion(
  state: AIAgentState,
  success: boolean,
  duration: number,
  quality: number,
): AIAgentState {
  return {
    ...state,
    performance: recordCompletionInternal(state.performance, success, duration, quality),
  };
}

/**
 * Agent's overall success rate (0-1).
 */
export function agentSuccessRate(state: AIAgentState): number {
  if (state.performance.tasksCompleted === 0) return 0;
  return state.performance.tasksSucceeded / state.performance.tasksCompleted;
}

/**
 * Agent's average quality score.
 */
export function agentAverageQuality(state: AIAgentState): number {
  return state.performance.averageQuality;
}

/**
 * Agent's average task duration.
 */
export function agentAverageDuration(state: AIAgentState): number {
  return state.performance.averageDuration;
}

/**
 * Determine if an agent should be retrained (quality declining below threshold).
 */
export function shouldRetrainAgent(state: AIAgentState): boolean {
  if (state.performance.tasksCompleted < 5) return false;
  const successRate = agentSuccessRate(state);
  const quality = state.performance.averageQuality;
  return successRate < 0.6 || quality < 0.4;
}
